<template>
  <div :id="componentId" class="guest-tab">
    <share-invoice-modal
      ref="shareInvoiceModal"
      :tabId="tab.id"
      :email="tabEmail"
    />

    <div class="guest-tab-window">
      <div v-if="!hideHeader" class="tab-header">
        <div v-if="tab.id" class="tab-id-header">
          <div class="header-label">
            {{ $t('guest_invoice_number').toUpperCase() }}
          </div>
          <span class="header-text">
            {{ tab.id }}
          </span>
        </div>

        <div class="balance-header">
          <div class="header-label">
            {{ $t('guest_balance_due').toUpperCase() }}
          </div>
          <span class="header-text">
            {{ formatMoney(balance_due) }}
          </span>
        </div>
      </div>

      <div v-if="!hideHeader && !!tab.id" class="tab-icons">
        <b-tooltip target="email-invoice" placement="top" title="Send Invoice">
          {{ $t("invoice_email_invoice") }}
        </b-tooltip>
        <b-tooltip target="view-invoice" placement="top" title="View Invoice">
          {{ $t("invoice_view_invoice") }}
        </b-tooltip>
        <i id="view-invoice" class="fi fi-page-pdf action-icon" @click="viewInvoice"></i>
        <i id="email-invoice" class="fi fi-mail action-icon" @click="emailInvoice"></i>
      </div>



      <!-- Purchases Table -->

      <div class="new-section-window tab-subsection">
        <b-collapse :id="`${ componentId }-charges-table`" class="mt-2" visible>
          <b-table
            class="tab-info-table"
            thead-class="hidden_header"
            hover
            :fields="chargesFields"
            :items="charges"
          >
            <template slot="cell(descriptor)" slot-scope="data" class="tab-item-name">
              <div>
                <span class="tab-item-name">
                  {{ data.item.charge.name }}
                </span>
                <span v-if="showDescription" class="tab-item-description">
                -  {{ data.item.charge.description }}
                </span>
                <span class="tab-item-name">
                  <i>&nbsp;x {{ data.item.quantity }}</i>
                </span>
              </div>
            </template>

            <template slot="cell(charge_rate)" slot-scope="data">
              <span class="guest-tab-item">
                <span class="item-total">
                  {{ formatMoney(data.item.total) }}
                </span>
                <i
                  v-if="removeCharge"
                  style="color: #aa0000;"
                  class="fi fi-x-circle"
                  @click="removeCharge(data.item.charge)"
                />
              </span>
            </template>
          </b-table>
        </b-collapse>

        <div v-b-toggle="`${ componentId }-charges-table`" class="table-collapse">
          <span class="when-opened">
            <i class="fi fi-minus small"></i>
          </span>
          <span class="when-closed">
            <i class="fi fi-plus small"></i>
          </span>
          <div class="tab-descriptor">
            <b class="collapse-item">
              <span class="descriptor-title">
                {{ $t('invoice_purchases').toUpperCase() }}
              </span>
              <span class="descriptor-value">
                {{ formatMoney(this.sub_total) }}
              </span>
            </b>
          </div>

        </div>
      </div>


      <!-- Taxes Table -->

      <b-collapse :id="`${ componentId }-taxes-table`" class="mt-2">
        <table class="tab-info-table custom-table">
          <tbody role="rowgroup" class="">
            <tr v-for="(tax, index) in taxes" :key="index" role="row">
              <td role="cell" aria-colindex="1" class="table-descriptor-cell">
                <div class="table-description">
                  <span class="tab-item-name">
                    {{ tax.description }}
                  </span>
                </div>
              </td>

              <td role="cell" aria-colindex="2" class="table-value-cell">
                <span class="guest-tab-item">
                  <span class="item-total" style="top: 6px; position: relative;">
                    {{ formatMoney(tax.rate) }}
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </b-collapse>

      <div v-b-toggle="`${ componentId }-taxes-table`" class="additionals-window tab-subsection">
        <span class="when-opened">
          <i class="fi fi-minus small"></i>
        </span>
        <span class="when-closed">
          <i class="fi fi-plus small"></i>
        </span>
        <div class="tab-descriptor">
          <span class="descriptor-title">
            {{ $t('invoice_taxes_fees').toUpperCase() }}</span>
          <span class="descriptor-value">
            {{ formatMoney(taxes_total) }}
          </span>
        </div>


        <!-- Gratuity Section -->
        <div class="tab-descriptor">
          <span class="descriptor-title">
            {{ $t('invoice_tips').toUpperCase() }}
          </span>
          <span class="descriptor-value">
            {{ formatMoney(gratuity) }}
          </span>
        </div>
      </div>

      <div class="tab-divider"></div>


      <div class="new-section-window tab-subsection">
        <div class="table-collapse">
          <div class="tab-descriptor">
            <span class="descriptor-title">
              {{ $t('invoice_total').toUpperCase() }}
            </span>
            <span class="descriptor-value">
              {{ formatMoney(sub_total + gratuity + taxes_total) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Discount Table Section -->
      <b-collapse :id="`${ componentId }-discounts-table`" class="mt-2">
        <table
          v-for="discount in discounts"
          :key="discount.id"
          class="tab-info-table custom-table"
        >
          <tbody role="rowgroup">
            <tr role="row">
              <td role="cell" aria-colindex="1" class="table-descriptor-cell">
                <div class="table-description">
                  <span class="tab-item-name">
                    {{ discount.date }} -
                  </span>
                  <span class="tab-item-name">
                    {{ discount.description }}
                  </span>
                </div>
              </td>

              <td role="cell" aria-colindex="2" class="table-value-cell">
                <span class="guest-tab-item">
                  <span class="item-total" style="top: 6px; position: relative;">
                    {{ formatMoney(discount.amount) }}
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </b-collapse>

      <div
        v-if="discount_total > 0"
        class="new-tab-section new-section-window tab-subsection"
      >
        <div v-b-toggle="`${ componentId }-discounts-table`" class="table-collapse">
          <span class="when-opened">
            <i class="fi fi-minus small"></i>
          </span>
          <span class="when-closed">
            <i class="fi fi-plus small"></i>
          </span>
          <div class="tab-descriptor">
            <b class="collapse-item">
              <span class="descriptor-title">
                {{ $t('invoice_discounts').toUpperCase() }}
              </span>
              <span class="descriptor-value">
                {{ formatMoney(discount_total) }}
              </span>
            </b>
          </div>
        </div>
      </div>

      <!-- Payments Table -->
      <b-collapse :id="`${ componentId }-payments-table`" class="mt-2">
        <table
          v-for="payment in payments"
          :key="payment.id"
          class="tab-info-table custom-table"
        >
          <tbody role="rowgroup" class="">
            <tr role="row">
              <td role="cell" aria-colindex="1" class="table-descriptor-cell">
                <div class="table-description">
                  <span class="tab-item-name">
                    {{ payment.date }} -
                  </span>
                  <span class="tab-item-name">
                    {{ payment.description }}
                  </span>
                </div>
              </td>

              <td role="cell" aria-colindex="2" class="table-value-cell">
                <span class="guest-tab-item">
                  <span class="item-total" style="top: 6px; position: relative;">
                    {{ formatMoney(payment.amount) }}
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </b-collapse>

      <div
        v-if="0 < payments.length"
        class="new-tab-section new-section-window tab-subsection"
      >
        <div v-b-toggle="`${ componentId }-payments-table`" class="table-collapse">
          <span class="when-opened">
            <i class="fi fi-minus small"></i>
          </span>
          <span class="when-closed">
            <i class="fi fi-plus small"></i>
          </span>
          <div class="tab-descriptor">
            <b class="collapse-item">
              <span class="descriptor-title">
                {{ $t('invoice_payments').toUpperCase() }}
              </span>
              <span class="descriptor-value">
                {{ formatMoney(payment_total) }}
              </span>
            </b>
          </div>
        </div>
      </div>

      <div v-if="refunded" class="tab-subsection">
        <div class="table-collapse">
          <div class="tab-descriptor">
            <b>
              <span class="descriptor-title">
                {{ $t('invoice_refunds').toUpperCase() }}
              </span>
              <span class="descriptor-value">
                {{ formatMoney(refunded) }}
              </span>
            </b>
          </div>
        </div>
      </div>

      <div class="new-section-window tab-subsection">
        <div class="table-collapse">
          <div class="tab-descriptor">
            <span class="descriptor-title">
              <b>{{ $t('invoice_balance_due').toUpperCase() }}</b>
            </span>
            <span class="descriptor-value">
              <b>{{ formatMoney(balance_due) }}</b>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatMoney } from '@/utils/number-utils';
import { formatDate } from '@/utils/date-helper';
import { ADJUSTMENT_TYPES, PAYMENT_TYPES } from '@/config/constants';

export default {
  name: 'GuestTab',

  components: {
    ShareInvoiceModal: () => import('@/components/modals/ShareInvoiceModal'),
  },

  props: {
    componentId: {
      type: String,
      default: 'guest-tab'
    },

    tab: {
      type: Object,
      default: () => {}
    },

    extraTip: {
      type: Number,
      default: 0
    },

    removeCharge: {
      type: Function,
      required: false,
      default: null
    },

    hideHeader: {
      type: Boolean,
      required: false,
      default: false
    },

    showDescription: {
      type: Boolean,
      required: false,
      default: true
    },

    tabEmail: {
      type: String,
      required: false,
      default: ''
    }
  },

  data () {
    return {
      chargesFields: [
        {
          key: 'descriptor',
          sortable: false,
          label: 'Payment',
          tdClass: 'table-descriptor-cell'
        },
        {
          key: 'charge_rate',
          sortable: false,
          label: 'Amount',
          tdClass: 'table-value-cell',
        }
      ],
      charges: [],
      taxes: [],
      discounts: [],
      payments: [],
      taxes_total: 0,
      gratuity: 0,
      refunded: 0,
      sub_total: 0,
      discount_total: 0,
      payment_total: 0,
      balance_due: 0
    };
  },

  computed: {
    ...mapGetters([
      'getVendor'
    ])
  },

  mounted () {
    if (this.tab && this.tab.stats) {
      this.setCharges();
      this.setTaxes();
      this.setDiscounts();
      this.setPayments();
      this.setStatsDetails();
    }
  },

  methods: {
    ...mapActions([
      'getInvoiceLink'
    ]),

    setCharges() {
      if (!this.tab.tab_item) {
        return [];
      }

      this.charges = [];

      this.tab.tab_item
        .filter(charge => {
          const charge_id = this.charges
            .findIndex(charge_ => (
              charge_.item_id === charge.item_id
            ));

          if (charge_id > -1) {
            this.charges[charge_id].quantity += 1;
            this.charges[charge_id].total += parseFloat(charge.rate);
            return;
          }

          this.charges.push({
            item_id: charge.item_id,
            quantity: 1,
            charge: charge,
            total: parseFloat(charge.rate)
          });
        });

      this.charges = this.charges
        .sort((a, b) => {
          if (a.charge.category_id === b.charge.category_id) {
            return a.charge.name > b.charge.name
              ? 1
              : -1;
          }

          return a.charge.category_id > b.charge.category_id
            ? 1
            : -1;
        });
    },

    setTaxes () {
      if (!this.tab.stats || !this.tab.stats.invoice_list) {
        return [];
      }

      this.taxes = [];

      this.tab.stats.invoice_list
        .map(item => item.taxes)
        .flat()
        .map(tax => {
          let index = this.taxes
            .findIndex(tax_ => (
              tax_.modifier_id === tax.modifier_id
            ));

          if (index > -1) {
            this.taxes[index].rate = this.taxes[index].rate + parseFloat(tax.rate);
            return;
          }

          this.taxes.push({
            name: tax.name,
            rate: parseFloat(tax.rate),
            modifier_id: tax.modifier_id,
            description: tax.description
          });
        });
    },

    setDiscounts () {
      if (!this.tab.tab_modifier) {
        return [];
      }

      this.discounts = this.tab.tab_modifier
        .filter(tabModifier => (
          tabModifier.category_id === PAYMENT_TYPES.DISCOUNT
        ))
        .map(discount => ({
          date: formatDate(discount.created_at, 'MMMM Do YYYY'),
          amount: discount.rate,
          description: discount.description
        }));
    },

    setPayments () {
      if (!this.tab.tab_modifier) {
        return [];
      }

      this.payments = this.tab.tab_modifier
        .filter(tabModifier => (
          tabModifier.category_id === PAYMENT_TYPES.PAYMENT ||
          tabModifier.category_id === PAYMENT_TYPES.DEPOSIT
        ))
        .map(payment => ({
          date: formatDate(payment.created_at, 'MMMM Do YYYY'),
          amount: payment.rate,
          description: payment.description
        }));
    },

    setStatsDetails () {
      this.taxes_total = this.tab.stats
        ? this.tab.stats.taxes_total
        : 0;

      this.gratuity = this.tab.stats
        ? this.tab.stats.gratuity + this.extraTip
        : 0;

      this.refunded = this.tab.stats
        ? Math.abs(this.tab.stats.refunded)
        : 0;

      this.sub_total = this.tab.stats
        ? this.tab.stats.sub_total
        : 0;

      this.discount_total = this.tab.stats
        ? this.tab.stats.discounts
        : 0;

      this.payment_total = this.tab.stats
        ? this.tab.stats.payments_made
        : 0;

      this.balance_due = this.tab.stats
        ? this.tab.stats.balance_due + this.extraTip
        : 0;
    },

    formatMoney (value) {
      return formatMoney(value, 2, this.getVendor.local_currency);
    },

    emailInvoice() {
      this.$refs.shareInvoiceModal.open();
    },

    async viewInvoice() {
      this.$Progress.start();
      this.infoNotification('Generating New Invoice');

      try {
        const invoiceLink = await this.getInvoiceLink(this.tab.id);
        window.open(invoiceLink, '_blank');
        this.$Progress.finish();

      } catch (e) {
        this.errorNotification(`Failed to generate invoice: ${e}`);
        this.$Progress.finish();
      }
    },
  },

  watch: {
    tab: function() {
      if (this.tab && this.tab.stats) {
        this.setCharges();
        this.setTaxes();
        this.setDiscounts();
        this.setPayments();
        this.setStatsDetails();
      }
    },

    extraTip: function() {
      this.setStatsDetails();
    }
  }
};
</script>

<style lang="scss">
.custom-table{
  width: 100%;
  padding-left: 8px;
  padding-right: 4px;
  padding-bottom: 6px;
}

.guest-tab {
  border: 1px solid #35353557;
  border-radius: 6px;
  max-width: 700px;
  margin: 1.5em auto;
  padding: 3px 0px;
  background-color: white;

  table {
    display: inline;
    text-align: center;
    float: left;
    background-color: white;

    tbody {
      width: 100%;
      display: inline-table;
      background-color: white;


      tr {
        width: 100%;
        display: inherit;
        cursor: initial;

        td {
          border: none;
        }

        .table-descriptor-cell {
          text-align: left;
        }

        .table-value-cell {
          text-align: right;
          vertical-align: inherit;
        }
      }
    }
  }

  .tab-header {
    position: relative;
    height: 3rem;
    margin: .5rem;
    width: calc(100% - 1rem);
    text-align: left;

    .header-text {
      font-size: 2rem;
      color: #777;
    }

    .header-label {
      margin-bottom: -0.5rem;
      color: #6378a6;
    }

    .balance-header {
      position: absolute;
      text-align: right;
      right: 0px;
      top: 0px;
    }
  }

  .fi-x-circle {
    cursor: pointer;
    font-size: 1rem;
    height: 40px;
  }

  .new-section-window {
    margin-top: 5px;
  }

  .guest-tab-window {
    overflow: auto;
    max-height: calc(50vh);

    .tab-subsection {
      display: inline-block;
      width: 100%;
      text-align: right;
    }

    .table-collapse {
      display: inline-block;
      width: 100%;
      cursor: pointer;

      .collapse-item {
        cursor: pointer;
      }
    }
  }

  .hidden_header {
    display: none;
  }

  .tab-item-label {
    float: left;
    margin-left: 25px;
  }

  .tab-item-amount {
    float: right;
    margin-right: 25px;
  }

  .tab-sums {
    float: right;
    font-weight: bold;
    margin: 10px 5px;

    span.tab-sum-item-total {
      float: right;
      padding-left: 50px;
    }
  }

  .download-tab.fi-download {
    font-size: 30px;
    float: left;
    margin: 0px 10px;
    cursor: pointer;
  }

  .tab-item-name {
    position: relative;
  }

  .tab-item-quantity {
    top: 6px;
    position: relative;
  }

  .table {
    margin-bottom: 0rem;
  }

  .table th,
  .table td {
    padding: 0.5rem;
    height: 2rem;
  }

  .tab-item-description {
    font-weight: 200;
    position: relative;
  }

  .tax-descriptor {
    letter-spacing: initial;
    font-size: small;
  }

  .guest-tab-item {
    top: 0px;
    position: relative;
    animation: fadeOut 500ms;
  }

  .guest-payment-item {
    top: 6px;
    position: relative;
    animation: fadeOut 500ms;
  }

  #charges-table {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
  }

  .tab-divider {
    border-top: 1px solid #00000050;
  }

  .tab-descriptor {
    padding: 0px 5px;
    width: 15rem;
    float: right;
    clear: both;
    cursor: initial;

    .descriptor-title {
      float: left;
      color: #6378a6;
      font-weight: bold;
    }
  }

  .tab-icons {
    float: left;
    font-size: 24px;
    cursor: initial;
    margin: 6px;

    .action-icon {
      margin: 3px;
      cursor: pointer;
    }
  }

  .tab-info-table {
    background-color: white;
  }
}

/* Plus Minus Toggle Buttons on Invoice */
.collapsed > .when-opened,
  :not(.collapsed) > .when-closed {
      display: none;
  }


</style>
